// ============================================================================
import { logger } from '@/lib/logger';
// SOUND WIZARD STUDIO - ERROR HANDLING SYSTEM
// ============================================================================
// Comprehensive error handling, logging, and recovery system
// Version: 2.1.0 - Advanced error classification and recovery strategies
// ============================================================================

import * as React from 'react'

// ============================================================================
// ERROR TYPES AND CLASSIFICATIONS
// ============================================================================

export enum ErrorType {
  AUDIO_CONTEXT = 'audio_context',
  WEB_AUDIO_API = 'web_audio_api',
  WEBGPU = 'webgpu',
  WEBAUDIO_WORKLET = 'webaudio_worklet',
  NETWORK = 'network',
  VALIDATION = 'validation',
  PERFORMANCE = 'performance',
  RENDERING = 'rendering',
  MEMORY = 'memory',
  BROWSER_SUPPORT = 'browser_support',
  UNKNOWN = 'unknown'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface SWS_Error {
  readonly id: string
  readonly type: ErrorType
  readonly severity: ErrorSeverity
  readonly message: string
  readonly originalError?: Error
  readonly context?: Record<string, any>
  readonly timestamp: number
  readonly userAgent: string
  readonly url: string
  readonly stackTrace?: string
  readonly recoverable: boolean
  readonly recoveryStrategies?: RecoveryStrategy[]
}

export interface RecoveryStrategy {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly execute: () => Promise<void> | void
  readonly priority: number
}

// ============================================================================
// ERROR FACTORY FUNCTIONS
// ============================================================================

/**
 * Creates a standardized SWS error
 */
export function createSWS_Error(
  type: ErrorType,
  severity: ErrorSeverity,
  message: string,
  options: {
    originalError?: Error
    context?: Record<string, any>
    recoverable?: boolean
    recoveryStrategies?: RecoveryStrategy[]
  } = {}
): SWS_Error {
  const {
    originalError,
    context = {},
    recoverable = false,
    recoveryStrategies = []
  } = options

  return {
    id: generateErrorId(),
    type,
    severity,
    message,
    originalError,
    context,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    stackTrace: originalError?.stack,
    recoverable,
    recoveryStrategies
  }
}

/**
 * Creates an audio context error
 */
export function createAudioContextError(
  originalError: Error,
  context?: Record<string, any>
): SWS_Error {
  return createSWS_Error(
    ErrorType.AUDIO_CONTEXT,
    ErrorSeverity.HIGH,
    'Audio context initialization failed',
    {
      originalError,
      context,
      recoverable: true,
      recoveryStrategies: [
        {
          id: 'retry_audio_context',
          name: 'Retry Audio Context',
          description: 'Attempt to reinitialize the audio context',
          execute: async () => {
            // Retry logic would be implemented here
            await new Promise(resolve => setTimeout(resolve, 1000))
          },
          priority: 1
        },
        {
          id: 'fallback_audio_context',
          name: 'Use Fallback Mode',
          description: 'Switch to fallback audio processing mode',
          execute: () => {
            // Fallback logic would be implemented here
            logger.log('Switching to fallback audio mode')
          },
          priority: 2
        }
      ]
    }
  )
}

/**
 * Creates a WebGPU error
 */
export function createWebGPU_Error(
  originalError: Error,
  context?: Record<string, any>
): SWS_Error {
  return createSWS_Error(
    ErrorType.WEBGPU,
    ErrorSeverity.MEDIUM,
    'WebGPU initialization or rendering failed',
    {
      originalError,
      context,
      recoverable: true,
      recoveryStrategies: [
        {
          id: 'fallback_canvas',
          name: 'Fallback to Canvas2D',
          description: 'Switch to Canvas2D rendering mode',
          execute: () => {
            logger.log('Switching to Canvas2D rendering')
          },
          priority: 1
        }
      ]
    }
  )
}

/**
 * Creates a network error
 */
export function createNetworkError(
  originalError: Error,
  url?: string,
  context?: Record<string, any>
): SWS_Error {
  return createSWS_Error(
    ErrorType.NETWORK,
    ErrorSeverity.MEDIUM,
    `Network request failed: ${url || 'unknown URL'}`,
    {
      originalError,
      context: { ...context, url },
      recoverable: true,
      recoveryStrategies: [
        {
          id: 'retry_request',
          name: 'Retry Request',
          description: 'Retry the network request',
          execute: async () => {
            await new Promise(resolve => setTimeout(resolve, 2000))
          },
          priority: 1
        }
      ]
    }
  )
}

// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================

/**
 * Generates a unique error ID
 */
function generateErrorId(): string {
  return `sws_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Logs an error with enhanced context
 */
export function logError(error: SWS_Error): void {
  const logData = {
    id: error.id,
    type: error.type,
    severity: error.severity,
    message: error.message,
    timestamp: new Date(error.timestamp).toISOString(),
    url: error.url,
    userAgent: error.userAgent,
    context: error.context,
    stackTrace: error.stackTrace,
    recoverable: error.recoverable
  }

  // Log to console with appropriate level
  switch (error.severity) {
    case ErrorSeverity.CRITICAL:
      logger.error('🚨 CRITICAL ERROR:', logData)
      break
    case ErrorSeverity.HIGH:
      logger.error('❌ HIGH PRIORITY ERROR:', logData)
      break
    case ErrorSeverity.MEDIUM:
      logger.warn('⚠️ MEDIUM PRIORITY ERROR:', logData)
      break
    case ErrorSeverity.LOW:
      logger.info('ℹ️ LOW PRIORITY ERROR:', logData)
      break
  }

  // In production, you would send this to an error reporting service
  if (process.env.NODE_ENV === 'production') {
    // sendToErrorReportingService(logData)
  }
}

/**
 * Attempts to recover from an error
 */
export async function attemptRecovery(error: SWS_Error): Promise<boolean> {
  if (!error.recoverable || !error.recoveryStrategies?.length) {
    return false
  }

  // Sort strategies by priority
  const strategies = [...error.recoveryStrategies].sort((a, b) => a.priority - b.priority)

  for (const strategy of strategies) {
    try {
      logger.log(`Attempting recovery strategy: ${strategy.name}`)
      await strategy.execute()
      logger.log(`Recovery strategy successful: ${strategy.name}`)
      return true
    } catch (recoveryError) {
      logger.warn(`Recovery strategy failed: ${strategy.name}`, recoveryError)
    }
  }

  return false
}

/**
 * Error boundary component for React
 */
export class ErrorBoundary extends React.Component<
  {
    children: React.ReactNode
    fallback?: React.ComponentType<{ error: SWS_Error; retry: () => void }>
    onError?: (error: SWS_Error) => void
  },
  { hasError: boolean; error?: SWS_Error }
> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: SWS_Error } {
    const swsError = createSWS_Error(
      ErrorType.UNKNOWN,
      ErrorSeverity.HIGH,
      error.message,
      {
        originalError: error,
        recoverable: true
      }
    )

    return { hasError: true, error: swsError }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const swsError = createSWS_Error(
      ErrorType.RENDERING,
      ErrorSeverity.HIGH,
      'React component error',
      {
        originalError: error,
        context: { errorInfo },
        recoverable: true
      }
    )

    logError(swsError)
    this.props.onError?.(swsError)
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return React.createElement(FallbackComponent, {
          error: this.state.error,
          retry: this.handleRetry
        })
      }

      return React.createElement('div', {
        className: "error-boundary p-6 bg-red-900/20 border border-red-500/30 rounded-lg text-center"
      }, [
        React.createElement('div', {
          key: 'title',
          className: "text-2xl mb-4"
        }, "🚨 Something went wrong"),
        React.createElement('div', {
          key: 'message',
          className: "text-red-300 mb-4"
        }, this.state.error.message),
        React.createElement('button', {
          key: 'retry',
          onClick: this.handleRetry,
          className: "px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
        }, "Try Again")
      ])
    }

    return this.props.children
  }
}

// ============================================================================
// HOOKS FOR ERROR HANDLING
// ============================================================================

/**
 * Hook for handling async errors
 */
export function useErrorHandler() {
  const [errors, setErrors] = React.useState<SWS_Error[]>([])

  const handleError = React.useCallback((error: SWS_Error) => {
    logError(error)
    setErrors(prev => [...prev, error])

    // Attempt automatic recovery for recoverable errors
    if (error.recoverable) {
      attemptRecovery(error).catch(recoveryError => {
        logger.error('Automatic recovery failed:', recoveryError)
      })
    }
  }, [])

  const clearError = React.useCallback((errorId: string) => {
    setErrors(prev => prev.filter(error => error.id !== errorId))
  }, [])

  const clearAllErrors = React.useCallback(() => {
    setErrors([])
  }, [])

  return {
    errors,
    handleError,
    clearError,
    clearAllErrors,
    hasErrors: errors.length > 0,
    hasCriticalErrors: errors.some(error => error.severity === ErrorSeverity.CRITICAL)
  }
}

/**
 * Hook for wrapping async operations with error handling
 */
export function useAsyncErrorHandler() {
  const { handleError } = useErrorHandler()

  const wrapAsync = React.useCallback(
    <T extends any[], R>(
      asyncFn: (...args: T) => Promise<R>,
      errorType: ErrorType = ErrorType.UNKNOWN,
      errorSeverity: ErrorSeverity = ErrorSeverity.MEDIUM
    ) => {
      return async (...args: T): Promise<R | null> => {
        try {
          return await asyncFn(...args)
        } catch (error) {
          const swsError = createSWS_Error(
            errorType,
            errorSeverity,
            error instanceof Error ? error.message : 'Unknown async error',
            {
              originalError: error instanceof Error ? error : undefined,
              context: { args },
              recoverable: true
            }
          )

          handleError(swsError)
          return null
        }
      }
    },
    [handleError]
  )

  return { wrapAsync }
}

// ============================================================================
// GLOBAL ERROR HANDLING
// ============================================================================

/**
 * Sets up global error handlers
 */
export function setupGlobalErrorHandlers(): () => void {
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const error = createSWS_Error(
      ErrorType.UNKNOWN,
      ErrorSeverity.HIGH,
      'Unhandled promise rejection',
      {
        context: { reason: event.reason },
        recoverable: false
      }
    )

    logError(error)
  }

  const handleGlobalError = (event: ErrorEvent) => {
    const error = createSWS_Error(
      ErrorType.UNKNOWN,
      ErrorSeverity.HIGH,
      event.message,
      {
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        },
        recoverable: false
      }
    )

    logError(error)
  }

  // Add global handlers
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  window.addEventListener('error', handleGlobalError)

  // Return cleanup function
  return () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    window.removeEventListener('error', handleGlobalError)
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Checks if an error is recoverable
 */
export function isRecoverableError(error: SWS_Error): boolean {
  return error.recoverable && !!error.recoveryStrategies?.length
}

/**
 * Gets user-friendly error message
 */
export function getUserFriendlyMessage(error: SWS_Error): string {
  switch (error.type) {
    case ErrorType.AUDIO_CONTEXT:
      return 'Audio system is not available. Please check your browser settings and try refreshing the page.'
    case ErrorType.WEBGPU:
      return 'GPU acceleration is not available. The application will use CPU rendering instead.'
    case ErrorType.NETWORK:
      return 'Network connection issue. Please check your internet connection and try again.'
    case ErrorType.BROWSER_SUPPORT:
      return 'Your browser may not support all features. Please try using a modern browser like Chrome or Firefox.'
    default:
      return 'An unexpected error occurred. Please try refreshing the page.'
  }
}

/**
 * Creates error recovery UI component
 */
export function ErrorRecoveryUI({
  error,
  onRetry
}: {
  error: SWS_Error
  onRetry: () => void
}) {
  const message = getUserFriendlyMessage(error)

  return React.createElement('div', {
    className: "error-recovery p-6 bg-yellow-900/20 border border-yellow-500/30 rounded-lg"
  }, React.createElement('div', {
    className: "flex items-start space-x-3"
  }, [
    React.createElement('div', {
      key: 'icon',
      className: "text-2xl"
    }, "⚠️"),
    React.createElement('div', {
      key: 'content',
      className: "flex-1"
    }, [
      React.createElement('h3', {
        key: 'title',
        className: "font-semibold text-yellow-300 mb-2"
      }, "Issue Detected"),
      React.createElement('p', {
        key: 'message',
        className: "text-yellow-200 mb-4"
      }, message),
      error.recoverable && React.createElement('button', {
        key: 'retry',
        onClick: onRetry,
        className: "px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors"
      }, "Try Again")
    ].filter(Boolean))
  ]))
}

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

export const errorUtils = {
  createSWS_Error,
  createAudioContextError,
  createWebGPU_Error,
  createNetworkError,
  logError,
  attemptRecovery,
  isRecoverableError,
  getUserFriendlyMessage,
  setupGlobalErrorHandlers
}

export default errorUtils